/** Supported site locales. `gb` is English (default, no URL prefix). */
export const SUPPORTED_LOCALES = ['gb', 'cn', 'ru', 'ar', 'ko'] as const

export type AppLocale = (typeof SUPPORTED_LOCALES)[number]

/** Locales that use a URL prefix (English has none). */
export const LOCALE_URL_PREFIXES = ['cn', 'ru', 'ar', 'ko'] as const

export type PrefixedLocale = (typeof LOCALE_URL_PREFIXES)[number]

/** Native language names — always shown as-is in the language picker. */
export const NATIVE_LOCALE_LABELS: Record<AppLocale, string> = {
  gb: 'English',
  cn: '中文',
  ru: 'Русский',
  ar: 'العربية',
  ko: '한국어',
}

export const LOCALE_HTML_LANG: Record<AppLocale, string> = {
  gb: 'en',
  cn: 'zh-CN',
  ru: 'ru',
  ar: 'ar',
  ko: 'ko',
}

export const LOCALE_OG: Record<AppLocale, string> = {
  gb: 'en_US',
  cn: 'zh_CN',
  ru: 'ru_RU',
  ar: 'ar_AR',
  ko: 'ko_KR',
}

/** Content JSON exists for these locales. */
export const CONTENT_LOCALES = new Set<AppLocale>(['gb', 'cn', 'ru', 'ar', 'ko'])

export function isAppLocale(value: unknown): value is AppLocale {
  return typeof value === 'string' && (SUPPORTED_LOCALES as readonly string[]).includes(value)
}

export function isPrefixedLocale(value: unknown): value is PrefixedLocale {
  return typeof value === 'string' && (LOCALE_URL_PREFIXES as readonly string[]).includes(value)
}

export function normalizeAppLocale(value: unknown): AppLocale {
  return isAppLocale(value) ? value : 'gb'
}

/** Locale used when fetching `/settings/.../*.${locale}.json`. */
export function resolveContentLocale(locale: string): AppLocale {
  const normalized = normalizeAppLocale(locale)
  return CONTENT_LOCALES.has(normalized) ? normalized : 'gb'
}

export function getUrlPrefix(locale: AppLocale): string {
  return isPrefixedLocale(locale) ? `/${locale}` : ''
}

/** Strip a leading locale prefix from a path, if present. */
export function stripLocalePrefix(path: string): string {
  for (const code of LOCALE_URL_PREFIXES) {
    if (path === `/${code}`) return '/'
    if (path.startsWith(`/${code}/`)) return path.slice(code.length + 1) || '/'
  }
  return path.startsWith('/') ? path : `/${path}`
}

/** Build a path for the given locale from the current route path. */
export function pathForLocale(path: string, locale: AppLocale): string {
  const base = stripLocalePrefix(path)
  const prefix = getUrlPrefix(locale)
  if (!prefix) return base
  if (base === '/') return prefix
  return `${prefix}${base}`
}

export function localeFromPath(path: string): AppLocale {
  for (const code of LOCALE_URL_PREFIXES) {
    if (path === `/${code}` || path.startsWith(`/${code}/`)) return code
  }
  return 'gb'
}

export function applyDocumentDirection(locale: AppLocale) {
  if (typeof document === 'undefined') return
  document.documentElement.dir = locale === 'ar' ? 'rtl' : 'ltr'
  document.documentElement.lang = LOCALE_HTML_LANG[locale]
}
